if (!customElements.get('cart-drawer')) {
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-cart-drawer-overlay]');
    this.closeButton = this.querySelector('[data-cart-drawer-close]');
    this.focusableElements = null;
    this.previousActiveElement = null;
  }

  connectedCallback() {
    this.attachEvents();
    this.subscribeToEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundHandleKeyDown);
    this.unsubscribers?.forEach(fn => fn?.());
  }

  attachEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButton?.addEventListener('click', () => this.close());
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeyDown);
  }

  subscribeToEvents() {
    this.unsubscribers = [
      theme.pubsub.subscribe(theme.pubsub.events.CART_ADD, () => this.open()),
      theme.pubsub.subscribe(theme.pubsub.events.CART_DRAWER_OPEN, () => this.open()),
      theme.pubsub.subscribe(theme.pubsub.events.CART_DRAWER_CLOSE, () => this.close()),
      theme.pubsub.subscribe(theme.pubsub.events.CART_UPDATE, (data) => this.handleCartUpdate(data)),
    ];
  }

  handleKeyDown(e) {
    if (!this.isOpen()) return;

    if (e.key === 'Escape') {
      this.close();
    }

    if (e.key === 'Tab') {
      this.handleTabKey(e);
    }
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', section: 'cart-drawer', selector: '[data-cart-drawer-content]' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setLoading(isLoading) {
    this.setAttribute('data-loading', isLoading ? 'true' : 'false');
  }

  renderContents(response) {
    this.renderSections(response.sections || response);
    this.updateHeaderBadge(response);
    this.open();
    this.updateFocusableElements();
  }

  renderSections(sections) {
    this.getSectionsToRender().forEach(({ id, section, selector }) => {
      const html = sections[id] || sections[section];
      if (!html) return;

      const doc = theme.utils.domParser.parseFromString(html, 'text/html');

      this.updateElement(selector, doc.querySelector(selector));
      this.updateFooter(doc);
      this.updateElement('[data-cart-count]', doc.querySelector('[data-cart-count]'), 'textContent');
    });
  }

  updateFooter(doc) {
    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = this.querySelector('.cart-drawer__footer');

    if (!newFooter && currentFooter) {
      currentFooter.remove();
      return;
    }

    if (newFooter && !currentFooter) {
      const panel = this.querySelector('.cart-drawer__panel');
      panel?.appendChild(newFooter.cloneNode(true));
      return;
    }

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }
  }

  updateElement(selector, newElement, mode = 'innerHTML') {
    const current = this.querySelector(selector);

    if (!newElement) {
      current?.remove();
      return;
    }

    if (!current) {
      this.querySelector('.cart-drawer__panel')?.appendChild(newElement.cloneNode(true));
      return;
    }

    if (mode === 'textContent') {
      current.textContent = newElement.textContent;
    } else {
      current.innerHTML = newElement.innerHTML;
    }
  }

  updateHeaderBadge(cart) {
    const headerBadge = document.getElementById('cart-count-badge');
    if (!headerBadge) return;

    const itemCount = cart?.item_count ?? 0;
    headerBadge.textContent = itemCount;
    headerBadge.classList.toggle('hidden', itemCount === 0);

    const liveRegion = document.querySelector('[data-cart-live-region]');
    if (liveRegion && typeof theme !== 'undefined' && theme.locales) {
      liveRegion.textContent = itemCount + ' ' + (theme.locales.items_in_cart || '');
    }
  }

  isOpen() {
    return this.getAttribute('data-open') === 'true';
  }

  lockScroll() {
    // iOS Safari ignores overflow:hidden for touch scrolling, so pin the body
    // with position:fixed (the Dawn-standard approach). Remember the scroll
    // offset to restore it on close.
    this._scrollLockY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollLockY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    if (this._scrollLockY != null) {
      window.scrollTo(0, this._scrollLockY);
      this._scrollLockY = null;
    }
  }

  open() {
    this.previousActiveElement = document.activeElement;
    this.setAttribute('data-open', 'true');
    document.querySelector('[data-cart-drawer-trigger]')?.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.updateFocusableElements();

    document.querySelectorAll('product-quick-add-modal[open]').forEach((m) => {
      m.removeAttribute('open');
      if (typeof m.close === 'function') m.close();
    });
    document.querySelectorAll('product-quick-add[open]').forEach((q) => {
      q.removeAttribute('open');
      if (typeof q.close === 'function') q.close();
    });
    document.querySelectorAll('product-card-toggle').forEach((toggle) => {
      const card = toggle._activeCard;
      if (!card) return;
      const quickAdd = card.querySelector('[open]');
      if (typeof toggle.close === 'function') {
        toggle.close(card, quickAdd);
        toggle._activeCard = null;
      }
    });
    document.querySelectorAll('.product-card__quick-add-panel.is-detached').forEach((panel) => {
      panel.remove();
    });
    document.querySelectorAll('.product-card.is-quick-add-open').forEach((card) => {
      card.classList.remove('is-quick-add-open');
    });
    document.documentElement.style.minHeight = '';

    setTimeout(() => {
      this.getFirstFocusableElement()?.focus();
    }, 100);
  }

  close() {
    this.setAttribute('data-open', 'false');
    document.querySelector('[data-cart-drawer-trigger]')?.setAttribute('aria-expanded', 'false');
    this.unlockScroll();

    if (this.previousActiveElement) {
      if (this.previousActiveElement.closest('product-quick-add')) {
        this.previousActiveElement.blur();
      } else {
        this.previousActiveElement.focus();
      }
      this.previousActiveElement = null;
    }
  }

  updateFocusableElements() {
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const elements = this.querySelectorAll(focusableSelectors);

    this.focusableElements = Array.from(elements).filter(this.isElementVisible);
  }

  isElementVisible(el) {
    return el.offsetWidth > 0 && el.offsetHeight > 0 && !el.disabled;
  }

  getFirstFocusableElement() {
    return this.focusableElements?.[0];
  }

  getLastFocusableElement() {
    return this.focusableElements?.[this.focusableElements.length - 1];
  }

  handleTabKey(e) {
    const firstElement = this.getFirstFocusableElement();
    const lastElement = this.getLastFocusableElement();

    if (e.shiftKey && document.activeElement === firstElement) {
      lastElement?.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      firstElement?.focus();
      e.preventDefault();
    }
  }

  async handleCartUpdate(data) {
    if (data?.skipFetch && data.cart?.item_count !== 0) {
      this.updateHeaderBadge(data.cart);
      return;
    }

    try {
      const sections = await this.fetchSections();
      this.renderSections(sections);
      this.updateHeaderBadge(data?.cart);

      this.updateFocusableElements();
    } catch (error) {
      console.error('Error updating cart:', error);
      theme.pubsub.publish(theme.pubsub.events.CART_ERROR, { error });
    }
  }

  async fetchSections() {
    const sectionIds = this.getSectionsToRender()
      .map(({ section }) => section)
      .join(',');

    const response = await fetch(`${window.Shopify.routes.root}?sections=${sectionIds}`);
    return await response.json();
  }
}

customElements.define('cart-drawer', CartDrawer);
}
